import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import AssignmentSelectionWmwPanel from './AssignmentSelectionWmwPanel';
import SelectedAssignmentPanel from './SelectedAssignmentPanel';
import StudentSelectionPanel from './StudentSelectionPanel';
import {
  getAssignmentBookList,
  getAssignmentList,
} from '../../../../api/utils/lms/assignment/api';
import {
  BookNode,
  SelectedAssignment,
} from '../../../../pages/lms/assignment-mgmt/CreateAssignment';
import { AssignmentType } from '../../../../constants/types/lms/type';

type AssignmentStep = 'ASSIGNMENT' | 'STUDENT';

/**
 * 개편 LMS (교사) - 과제 관리 > 과제 내주기 페이지 > WMW 콘텐츠 영역
 */
const CreateAssignmentWmwWrapper = () => {
  const { classId } = useParams();

  const [bookNodes, setBookNodes] = useState<BookNode[]>([]);
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<
    Set<number>
  >(new Set());

  const [openedBookIds, setOpenedBookIds] = useState<Set<string>>(new Set());

  const [step, setStep] = useState<AssignmentStep>('ASSIGNMENT');

  useEffect(() => {
    const fetchInitialBooks = async () => {
      const booksWMW = await getAssignmentBookList({ classId, type: 'WMW' });
      const booksJS1 = await getAssignmentBookList({ classId, type: 'JS1' });
      const booksJS2 = await getAssignmentBookList({ classId, type: 'JS2' });

      const booksTotal = [
        ...(booksWMW ?? []).map((title) => ({
          title,
          type: AssignmentType.WMW,
        })),
        ...(booksJS1 ?? []).map((title) => ({
          title,
          type: AssignmentType.JS1,
        })),
        ...(booksJS2 ?? []).map((title) => ({
          title,
          type: AssignmentType.JS2,
        })),
      ];

      const books = booksTotal.map((book) => ({
        id: book.type + '-' + Number(book.title.match(/\d+$/)?.[0]),
        title: book.title,
        type: book.type,
      }));

      if (books.length === 0) {
        setBookNodes([]);
        return;
      }

      const firstBook = books[0];
      const firstBookAssignments = await getAssignmentList({
        classId,
        type: firstBook.type,
        book: firstBook.title,
      });

      const bookNodes: BookNode[] = books.map((book) =>
        book.id === firstBook.id
          ? {
              ...book,
              isAssignmentLoaded: true,
              assignments: firstBookAssignments,
            }
          : book,
      );

      setBookNodes(bookNodes);

      setOpenedBookIds(new Set([firstBook.id]));
    };

    fetchInitialBooks();
  }, [classId]);

  const selectedAssignments = useMemo<SelectedAssignment[]>(() => {
    return bookNodes.flatMap(
      (book) =>
        book.assignments
          ?.filter((assignment) => selectedAssignmentIds.has(assignment.id))
          .map((assignment) => ({
            ...assignment,
            bookTitle: book.title,
          })) ?? [],
    );
  }, [bookNodes, selectedAssignmentIds]);

  const handleBookClick = async (bookId: string) => {
    const targetBook = bookNodes.find((book) => book.id === bookId);

    if (!targetBook || targetBook.isAssignmentLoaded) return;

    const assignments = await getAssignmentList({
      classId,
      type: targetBook.type,
      book: targetBook.title,
    });

    setBookNodes((prev) =>
      prev.map((book) =>
        book.id === bookId
          ? {
              ...book,
              isAssignmentLoaded: true,
              assignments,
            }
          : book,
      ),
    );
  };

  const handleBookSelect = async (bookId: string) => {
    const targetBook = bookNodes.find((book) => book.id === bookId);
    if (!targetBook) return;

    let assignments = targetBook.assignments;

    if (!targetBook.isAssignmentLoaded) {
      assignments = await getAssignmentList({
        classId,
        type: targetBook.type,
        book: targetBook.title,
      });

      setBookNodes((prev) =>
        prev.map((book) =>
          book.id === bookId
            ? {
                ...book,
                isAssignmentLoaded: true,
                assignments,
              }
            : book,
        ),
      );
    }

    const assignmentIds = assignments?.map((assignment) => assignment.id) ?? [];

    setSelectedAssignmentIds((prev) => {
      const next = new Set(prev);
      const allSelected = assignmentIds.every((id) => next.has(id));

      assignmentIds.forEach((id) => {
        if (allSelected) {
          next.delete(id);
        } else {
          next.add(id);
        }
      });

      return next;
    });

    setOpenedBookIds((prev) => {
      const next = new Set(prev);
      next.add(bookId);
      return next;
    });
  };

  const handleBookOpen = async (bookId: string) => {
    const isOpened = openedBookIds.has(bookId);

    if (isOpened) {
      setOpenedBookIds((prev) => {
        const next = new Set(prev);
        next.delete(bookId);
        return next;
      });

      return;
    }

    await handleBookClick(bookId);

    setOpenedBookIds((prev) => {
      const next = new Set(prev);
      next.add(bookId);
      return next;
    });
  };

  const handleAssignmentSelect = (assignmentId: number) => {
    setSelectedAssignmentIds((prev) => {
      const next = new Set(prev);

      if (next.has(assignmentId)) {
        next.delete(assignmentId);
      } else {
        next.add(assignmentId);
      }

      return next;
    });
  };

  const removeSelectedAssignment = (assignmentId: number) => {
    setSelectedAssignmentIds((prev) => {
      const next = new Set(prev);
      next.delete(assignmentId);
      return next;
    });
  };

  const handleStudentSelectClick = (
    selectedAssignments: SelectedAssignment[],
  ) => {
    if (selectedAssignments.length === 0) return;

    setStep('STUDENT');
  };

  return (
    <>
      {step === 'ASSIGNMENT' ? (
        <div className="my-5 grid grid-cols-1 gap-4 px-3 xl:grid-cols-[1fr_40%] xl:px-0">
          <AssignmentSelectionWmwPanel
            bookNodes={bookNodes}
            selectedAssignmentIds={selectedAssignmentIds}
            openedBookIds={openedBookIds}
            onBookOpen={handleBookOpen}
            onBookSelect={handleBookSelect}
            onAssignmentSelect={handleAssignmentSelect}
          />

          <SelectedAssignmentPanel
            selectedAssignments={selectedAssignments}
            onRemove={removeSelectedAssignment}
            onStudentSelect={handleStudentSelectClick}
          />
        </div>
      ) : (
        <div className="my-5 px-3 xl:px-0">
          <StudentSelectionPanel
            selectedAssignments={selectedAssignments}
            onInit={() => {
              setSelectedAssignmentIds(new Set());
              setOpenedBookIds(
                bookNodes[0] ? new Set([bookNodes[0].id]) : new Set(),
              );
            }}
            onBack={() => setStep('ASSIGNMENT')}
          />
        </div>
      )}
    </>
  );
};

export default CreateAssignmentWmwWrapper;
